import { gameState } from '../state/gameState.js';
import { messageFor } from '../api/errors.js';
import { showActionsModal } from './Modal.js';
import { showToast } from './Toast.js';
import { NOZZLES, fuelOptions, litres, nozzleLabel, zlPerL } from './fuelOptions.mjs';

// The pump. Two steps, because that is how a real one works: pick the nozzle, then pick
// how much — and the game only gets its joke if the first step tells you NOTHING about
// which nozzle this car drinks. The wrong one pumps happily, costs real money, and kills
// the engine 100 m down the road (CarController owns that part).
//
//   const out = await showFuelModal(scene, { ...car, fuel: fuelLocal }, gameState.raw.catalog);
//   if (out?.refuel?.misfueled) …
//
// Pass the car record with the tank level you actually believe in: while driving that is
// the controller's `fuelLocal`, which is ahead of the record between drive reports.

/**
 * Two-step petrol station modal. Resolves with the `carsRefuel` response
 * (`{ refuel: { misfueled, liters, cost }, …state }`) or null if nothing was pumped.
 */
export async function showFuelModal(scene, car, catalog = gameState.raw?.catalog) {
  const spec = catalog?.cars?.[car?.model];
  if (!car || !spec) return null;

  // EVs charge at home. Say so instead of opening a modal that could only disappoint.
  if (spec.fuel === 'ev') {
    showToast(scene, messageFor({ detail: { error_code: 'CAR_IS_ELECTRIC' } }));
    return null;
  }

  const prices = catalog?.fuel ?? {};
  const capacity = spec.capacity ?? 0;
  const fuel = Math.max(0, Math.min(car.fuel ?? 0, capacity));
  const name = car.name_pl ?? spec.name_pl ?? 'Auto';

  const nozzle = await showActionsModal(scene, {
    title: '{fuel} Stacja paliw',
    body: `${name} · bak: ${litres(fuel)} / ${litres(capacity)}`,
    actions: NOZZLES.map((n) => ({
      label: nozzleLabel(n, prices[n.nozzle]),
      fn: () => n.nozzle,
    })),
  });
  if (!nozzle) return null;

  const chosen = NOZZLES.find((n) => n.nozzle === nozzle);
  const price = prices[nozzle] ?? 0;
  // The server caps every amount at what the wallet covers, so the buttons must too —
  // otherwise „Do pełna (65 l · 390 zł)” bills 40 zł and pumps 6 litres.
  const money = gameState.raw?.money ?? 0;
  const options = fuelOptions({ fuel, capacity, price, money });

  return showActionsModal(scene, {
    title: 'Ile zatankować?',
    body: `${chosen.icon} ${chosen.name} · ${zlPerL(price)} · w baku ${litres(fuel)} / ${litres(capacity)}`,
    actions: options.map((o) => ({
      label: o.label,
      disabled: o.disabled,
      fn: async () => {
        try {
          return await gameState.carsRefuel(car.id, nozzle, o.liters);
        } catch (e) {
          showToast(scene, messageFor(e));
          return null;
        }
      },
    })),
  });
}
